<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_LOAN_COLLECTIONS_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport()">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport()">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="accountBalanceTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(disbursedAmount)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(totalPrincipalOutstanding)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(principalDueAndUnpaid)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(interestDueAndUnpaid)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(lastRepaymentAmount)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(principalPaidOnDueDate)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(principalRecoveredAfterDueDate)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(interestPaidOnDueDate)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(interestRecoveredAfterDueDate)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(feesPaidOnDueDate)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(loanPenaltyCharged)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(lastRepaymentDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(loanStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="accountBalanceMovementRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Loan Account Name')"
                type="text"
                for="account-name"
              />
            </b-col>
            <!-- Account Name -->
            <!-- Loan Account Number -->
            <b-col md="6">
              <InputField
                id="loan-account-number"
                v-model="filter.loanAccountNumber"
                :label="$t('Loan Account Number')"
                :placeholder="$t('Enter Loan Account Number')"
                type="text"
                for="loan-account-number"
              />
            </b-col>
          <!-- Loan Account Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                rules="required"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                rules="required"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
          <!-- End Date -->
          </b-row>

          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product Name')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="filter.accountOfficerStaffId"
                :label="$t('Account Officer')"
                :options="accountOfficerOptions"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
              />
            </b-col>
            <b-col md="6">
              <SwitchButton
                v-model="filter.loanStatus"
                :label="$t('Closed Loans')"
                name="closed-loan"
                :checked="false"
                class="mt-2"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import * as FileSaver from 'file-saver'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount, toFormatAmount } from '@/helpers/formatAmount'
import toNumber from '@/helpers/toNumber'
import getStatusColor from '../../../../helpers/getStatusColor'
import { toCustomDate, toDateWithYearFirst } from '@/helpers/formatDate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BBadge,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SwitchButton,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      formatAmount,
      toBalance,
      toFormatAmount,
      toDateWithYearFirst,
      getStatusColor,
      query: '',
      currentDate: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      isDisabled: true,
      required,
      modalTitle: this.$t('Loan Collections Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Number' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'loanAccountNumber', sortable: true, label: 'Loan Account Number' },
        { key: 'linkedNumber', sortable: true, label: 'Linked Number' },
        { key: 'product', sortable: true, label: 'Product' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'disbursedAmount', sortable: true, label: 'Disbursed Amount' },
        { key: 'totalPrincipalOutstanding', sortable: true, label: 'Total Outstanding Principal' },
        { key: 'principalDueAndUnpaid', sortable: true, label: 'Principal Due & Unpaid' },
        { key: 'interestDueAndUnpaid', sortable: true, label: 'Interest Due & Unpaid' },
        { key: 'principalPaidOnDueDate', sortable: true, label: 'Principal Paid on Due Date' },
        { key: 'principalRecoveredAfterDueDate', sortable: true, label: 'Principal Recovered after Due Date' },
        { key: 'interestPaidOnDueDate', sortable: true, label: 'Interest Paid on Due Date' },
        { key: 'interestRecoveredAfterDueDate', sortable: true, label: 'Interest Recovered after Due Date' },
        { key: 'feesPaidOnDueDate', sortable: true, label: 'Fees Paid on Due Date' },
        { key: 'loanPenaltyCharged', sortable: true, label: 'Loan Penalty Charged' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'lastRepaymentDate', sortable: true, label: 'Last Repaid Date' },
        { key: 'lastRepaymentAmount', sortable: true, label: 'Last Repaid Amount' },
        { key: 'guarantor', sortable: true, label: 'Guarantor Name' },
        { key: 'guarantorPhoneNumber', sortable: true, label: 'Guarantor Phone Number' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'loanStatus', sortable: true, label: 'Status' },

      ],
      filter: {
        loanAccountNumber: '',
        accountName: '',
        financialDateFrom: null,
        financialDateTo: null,
        productCode: '',
        branchCode: '',
        accountOfficerStaffId: '',
        loanStatus: false,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      productOptions: [],
      branchOptions: [],
      accountOfficerOptions: [],
      reportData: [],
      allSearchedData: [],
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLoanCollectionsReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLoanCollectionsReport()
    },
  },
  mounted() {
    // this.fetchLoanCollectionsReport()
    this.fetchBranches()
    this.fetchProduct()
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranch: 'operations/getBranchList',
      getLoanCollections: 'reports/getLoanCollections',
      downloadLoanCollections: 'reports/downloadLoanCollections',
      getAccountOfficers: 'account/getAccountOfficerList',
    }),
    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_COLLECTIONS_REPORT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.fetchLoanCollectionsReport()
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_COLLECTIONS_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: { type: 'Loan' },
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_COLLECTIONS_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.accountBalanceMovementRules.validate().then(async success => {
        if (success) {
          this.fetchLoanCollectionsReport()
        }
      })
    },
    async fetchLoanCollectionsReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getLoanCollections({
          search: {
            ...this.filter,
            financialDateFrom: this.filter.financialDateFrom ? toDateWithYearFirst(this.filter.financialDateFrom) : null,
            financialDateTo: this.filter.financialDateTo ? toDateWithYearFirst(this.filter.financialDateTo) : null,
            loanStatus: this.filter.loanStatus === false ? 'Active' : '',
          },
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadLoanCollections({
          search: {
            ...this.filter,
            financialDateFrom: this.filter.financialDateFrom ? toDateWithYearFirst(this.filter.financialDateFrom) : null,
            financialDateTo: this.filter.financialDateTo ? toDateWithYearFirst(this.filter.financialDateTo) : null,
            loanStatus: this.filter.loanStatus === false ? 'Active' : '',
          },
          page: 1,
          limit: 100000,
          fileExportFormat: 1,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'Loan Collection Report.csv')
          this.alertSuccess({ text: 'Your request to download Loan Collections Report was successful' })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errorMessage = `Your request to download Loan Collections Report failed. Reason: ${responseError.errorMessage}`
          this.alertError({ text: errorMessage })
        })
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadLoanCollections({
          search: {
            ...this.filter,
            financialDateFrom: this.filter.financialDateFrom ? toDateWithYearFirst(this.filter.financialDateFrom) : null,
            financialDateTo: this.filter.financialDateTo ? toDateWithYearFirst(this.filter.financialDateTo) : null,
            loanStatus: this.filter.loanStatus === false ? 'Active' : '',
          },
          page: 1,
          limit: 100000,
          fileExportFormat: 0,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'Loan Collection Report.xlsx')
          this.alertSuccess({ text: 'Your request to download Loan Collection Report was successful' })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errorMessage = `Your request to download Loan Collections Report failed. Reason: ${responseError.errorMessage}`
          this.alertError({ text: errorMessage })
        })
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
